import * as z from 'zod';

import { TeamPermissionsId } from '../BrandedIds';

const LoanStatusManagement = z.object({
  createLoans: z.boolean(),
  deleteLoans: z.boolean().default(false),
  updateLoanStatus: z.boolean(),
  updateStopSettings: z.boolean(),
  updateConstructionToPerm: z.boolean(),
  updateCalls: z.boolean(),
  manageLoanPointOfContacts: z.boolean(),
});

const LoanDataAndBalances = z.object({
  updateLoanData: z.boolean(),
  updateInterestRate: z.boolean(),
  updateMaturityDate: z.boolean(),
  createReports: z.boolean(),
  updateLoanIdsAndRemittance: z.boolean(),
  updateAuthorizedContacts: z.boolean(),
  createDepositsOrTransfers: z.boolean(),
  createPrincipalModifications: z.boolean(),
  createWriteOffs: z.boolean(),
});

const PaymentsDisbursementsFees = z.object({
  createPayments: z.boolean(),
  updateBankingDetails: z.boolean(),
  createFees: z.boolean(),
  createDisbursementsFromServicingTransfer: z.boolean(),
  createDisbursementsFromEscrow: z.boolean(),
  createDisbursementsFromAdvance: z.boolean(),
  createDisbursementsFromPayoff: z.boolean(),
  createDisbursementsFromDraw: z.boolean(),
});

const EscrowManagement = z.object({
  createEscrowAnalysis: z.boolean(),
  updateEscrowDetails: z.boolean(),
  managePayees: z.boolean().default(false),
});

const DocumentsAndForms = z.object({
  createLoanDocuments: z.boolean(),
  createManualStatements: z.boolean(),
  createPayoffStatements: z.boolean(),
  createForm1098s: z.boolean(),
});

const Delinquency = z.object({
  createWorkoutPlans: z.boolean(),
  createDefaultInterest: z.boolean(),
  createReportableEvents: z.boolean(),
});

const IntegrationsAndCredentials = z.object({
  // Existing roles with SFTP-adjacent permissions are backfilled to true via
  // migration so customers don't lose access on rollout.
  manageSftpCredentials: z.boolean().default(false),
});

export const TeamPermissionsActions = z.object({
  ...LoanStatusManagement.shape,
  ...LoanDataAndBalances.shape,
  ...PaymentsDisbursementsFees.shape,
  ...EscrowManagement.shape,
  ...DocumentsAndForms.shape,
  ...Delinquency.shape,
  ...IntegrationsAndCredentials.shape,
});
export type TeamPermissionsActions = z.infer<typeof TeamPermissionsActions>;

export const TeamPermissions = z.object({
  id: TeamPermissionsId,
  actions: TeamPermissionsActions,
});
export type TeamPermissions = z.infer<typeof TeamPermissions>;

export const getDefaultTeamPermissionActions = (defaultValue = false): TeamPermissionsActions => ({
  createLoans: defaultValue,
  deleteLoans: defaultValue,
  createPayments: defaultValue,
  createFees: defaultValue,
  createDepositsOrTransfers: defaultValue,
  createDisbursementsFromEscrow: defaultValue,
  createDisbursementsFromServicingTransfer: defaultValue,
  createDisbursementsFromAdvance: defaultValue,
  createDisbursementsFromPayoff: defaultValue,
  createDisbursementsFromDraw: defaultValue,
  manageLoanPointOfContacts: defaultValue,

  createPayoffStatements: defaultValue,
  createLoanDocuments: defaultValue,
  createWorkoutPlans: defaultValue,
  createReports: defaultValue,
  createDefaultInterest: defaultValue,
  createPrincipalModifications: defaultValue,
  createWriteOffs: defaultValue,
  createManualStatements: defaultValue,

  updateAuthorizedContacts: defaultValue,
  updateMaturityDate: defaultValue,
  updateInterestRate: defaultValue,
  updateLoanStatus: defaultValue,
  updateLoanIdsAndRemittance: defaultValue,
  updateLoanData: defaultValue,
  updateConstructionToPerm: defaultValue,
  updateStopSettings: defaultValue,
  updateCalls: defaultValue,

  createForm1098s: defaultValue,
  createEscrowAnalysis: defaultValue,
  updateBankingDetails: defaultValue,
  updateEscrowDetails: defaultValue,
  createReportableEvents: defaultValue,
  managePayees: defaultValue,
  manageSftpCredentials: defaultValue,
});
